import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Check, X, RefreshCw, Trash2, Copy, CreditCard, KeyRound } from "lucide-react";
import { useAuth } from "../lib/auth";
import { copyToClipboard } from "../lib/clipboard";
import ChangePasswordModal from "../components/ChangePasswordModal";
import UsageAnalytics from "../components/UsageAnalytics";

export default function OrgAdmin() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [managers, setManagers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [upgradingTo, setUpgradingTo] = useState(null);
  const [search, setSearch] = useSearchParams();
  const [showChangePw, setShowChangePw] = useState(false);
  const [resetConfirmId, setResetConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [orgRes, mgrRes, planRes] = await Promise.all([
        api.get("/org/me"),
        api.get("/org/managers"),
        api.get("/billing/plans").catch(() => ({ data: [] })),
      ]);
      setMe(orgRes.data);
      setManagers(mgrRes.data);
      setPlans(planRes.data || []);
    } catch (e) {
      toast.error(apiError(e, "Failed to load org"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll Stripe checkout status when redirected back with ?session_id=...
  // Refs let us reference the latest `search`/`setSearch`/`load` closures
  // inside the poll loop without listing them as deps (which would tear
  // down the poller on every URL query mutation and duplicate the polling).
  const searchRef = useRef(search);
  const setSearchRef = useRef(setSearch);
  const loadRef = useRef(load);
  searchRef.current = search;
  setSearchRef.current = setSearch;
  loadRef.current = load;

  useEffect(() => {
    const currentSearch = searchRef.current;
    const currentSetSearch = setSearchRef.current;
    if (currentSearch.get("checkout") === "cancelled") {
      toast.message("Checkout cancelled");
      currentSetSearch({}, { replace: true });
      return;
    }
    const sid = currentSearch.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          // Fire Google Ads "Purchase" conversion exactly once per paid session.
          // Guarded with sessionStorage so a subsequent page reload (which the
          // poller never reaches because we strip the query param right after)
          // can't double-fire. transaction_id = Stripe session id, which Ads
          // uses to dedupe if the same user reloads /admin/org?session_id=...
          try {
            const fired = sessionStorage.getItem(`ads_conv_${sid}`);
            if (!fired && typeof window.gtag === "function") {
              const valueAud = (r.data.amount_total || 0) / 100;
              const currency = (r.data.currency || "aud").toUpperCase();
              window.gtag("event", "conversion", {
                send_to: "AW-809141827/1GVICLn0kqYcEMOM6oED",
                value: valueAud,
                currency,
                transaction_id: sid,
              });
              sessionStorage.setItem(`ads_conv_${sid}`, "1");
            }
          } catch { /* analytics must never break the user flow */ }
          toast.success("Payment received — your subscription is active");
          setSearchRef.current({}, { replace: true });
          await loadRef.current();
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Checkout session expired");
          setSearchRef.current({}, { replace: true });
          return;
        }
        if (attempts >= 5) {
          toast.message("Payment is processing — refresh shortly to confirm.");
          setSearchRef.current({}, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        toast.error(apiError(e, "Could not verify payment"));
        setSearchRef.current({}, { replace: true });
      }
    };
    toast.message("Verifying payment…");
    poll();
  }, []);

  const act = async (mgrId, fn, label) => {
    setBusyId(mgrId);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusyId(null);
    }
  };

  const doResetPassword = async (mgr) => {
    setBusyId(mgr.id);
    try {
      const r = await api.post(`/org/managers/${mgr.id}/reset-password`);
      if (r.data.email_sent) {
        toast.success(`Reset link emailed to ${mgr.email}`);
      } else {
        toast.success("Reset link created (email delivery could not be confirmed)");
      }
    } catch (e) {
      toast.error(apiError(e, "Failed to send reset link"));
    } finally {
      setBusyId(null);
      setResetConfirmId(null);
    }
  };

  const rotateCode = async () => {
    setRotating(true);
    try {
      const r = await api.post("/org/join-code/rotate");
      toast.success(`New join code: ${r.data.code}`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not rotate"));
    } finally {
      setRotating(false);
    }
  };

  const copy = async (s) => {
    const ok = await copyToClipboard(s);
    if (ok) toast.success("Copied");
    else toast.error("Copy blocked by browser — please select and copy manually");
  };

  if (!user?.is_org_admin) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="org-admin-forbidden">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[FORBIDDEN]</div>
        <h1 className="font-display font-bold text-3xl">Org admin access required</h1>
      </div>
    );
  }

  if (loading || !me) {
    return <div className="p-10 font-mono-cal text-xs">[LOADING ORG…]</div>;
  }

  const { org, plan, usage } = me;
  const pending = managers.filter((m) => !m.is_authorized);
  const active = managers.filter((m) => m.is_authorized);
  const overLimit = plan && usage.managers >= (plan.max_managers ?? 0);
  const paidPlans = plans.filter((p) => (p.monthly_cost ?? 0) > 0);
  const periodEnd = org.subscription_period_end ? new Date(org.subscription_period_end).toLocaleDateString() : null;

  const upgrade = async (planId) => {
    setUpgradingTo(planId);
    try {
      const r = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Could not start checkout");
    } catch (e) {
      toast.error(apiError(e, "Checkout failed"));
    } finally {
      setUpgradingTo(null);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel auto-renewal? Your subscription stays active until the end of the current billing period — no refund, no proration. After that date, create-actions will be disabled until you re-subscribe.")) return;
    try {
      const r = await api.post("/billing/cancel");
      const date = r.data?.subscription_period_end ? new Date(r.data.subscription_period_end).toLocaleDateString() : "the end of your period";
      toast.success(`Cancellation scheduled. Access continues until ${date}.`);
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not cancel"));
    }
  };

  const resumeSub = async () => {
    try {
      await api.post("/billing/resume");
      toast.success("Auto-renewal resumed.");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not resume"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12" data-testid="org-admin-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-3">[ORG ADMIN]</div>
      <h1 className="font-display font-bold text-5xl tracking-tight mb-2">{org.name}</h1>
      <p className="text-[#666] mb-10">Manage your team, approvals, and plan limits.</p>

      <div className="grid md:grid-cols-3 gap-6 mb-10">
        <div className="cal-card p-6" data-testid="org-plan-card">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[PLAN]</div>
          <div className="font-display font-bold text-2xl">{plan?.name || "—"}</div>
          <div className="text-sm text-[#666] mt-2">A${plan?.monthly_cost ?? 0}/mo</div>
          <div className="font-mono-cal text-xs text-[#666] mt-3">Status: <span className="text-[#111] uppercase">{org.status}</span></div>
        </div>
        <div className="cal-card p-6" data-testid="org-usage-card">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[MANAGERS]</div>
          <div className="font-display font-bold text-3xl">{usage.managers}<span className="text-xl text-[#666]"> / {plan?.max_managers ?? "∞"}</span></div>
          <div className="text-sm text-[#666] mt-2">{usage.pending_managers} pending approval</div>
        </div>
        <div className="cal-card p-6" data-testid="org-employees-card">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[EMPLOYEES]</div>
          <div className="font-display font-bold text-3xl">{usage.employees}<span className="text-xl text-[#666]"> / {plan?.max_employees ?? "∞"}</span></div>
          <div className="text-sm text-[#666] mt-2">across all profiles</div>
        </div>
      </div>

      <div className="mb-10">
        <UsageAnalytics scope="org" />
      </div>

      <div className="cal-card p-6 mb-10" data-testid="org-billing-card">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">[BILLING]</div>
            <div className="font-display font-bold text-xl">
              {plan?.name}
              <span className="ml-3 font-mono-cal text-xs uppercase text-[#FF4F00]">{org.status}</span>
              {org.cancel_at_period_end && (
                <span className="ml-3 font-mono-cal text-xs uppercase text-[#FF4F00]" data-testid="cancel-pending-badge">[CANCELS AT PERIOD END]</span>
              )}
            </div>
            <p className="text-sm text-[#666] mt-1">
              {org.status === "trialing" && org.trial_ends_at && `Trial ends ${new Date(org.trial_ends_at).toLocaleDateString()}.`}
              {org.status === "active" && !org.cancel_at_period_end && periodEnd && `Renews automatically on ${periodEnd}.`}
              {org.status === "active" && org.cancel_at_period_end && periodEnd && `Access continues until ${periodEnd}, then ends.`}
              {org.status === "past_due" && "Last payment failed — please update your card to continue."}
              {org.status === "canceled" && "Subscription cancelled. Choose a plan below to reactivate."}
              {org.status === "suspended" && "Subscription suspended. Contact support."}
            </p>
          </div>
          {(org.stripe_subscription_id || org.stripe_customer_id) && (
            <div className="flex items-center gap-2">
              {org.cancel_at_period_end ? (
                <button onClick={resumeSub} className="cal-btn" data-testid="resume-sub-btn">
                  Resume auto-renewal
                </button>
              ) : org.status === "active" || org.status === "trialing" ? (
                <button onClick={cancelSub} className="cal-btn cal-btn-ghost" data-testid="cancel-sub-btn">
                  Cancel subscription
                </button>
              ) : null}
            </div>
          )}
        </div>
        {paidPlans.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {paidPlans.map((p) => {
              const isCurrent = p.id === plan?.id && org.status === "active";
              return (
                <button
                  key={p.id}
                  onClick={() => !isCurrent && upgrade(p.id)}
                  disabled={!!upgradingTo || isCurrent}
                  className={`text-left border ${isCurrent ? "border-[#00A36C]" : "border-[#111]/30 hover:border-[#FF4F00]"} p-4 transition disabled:opacity-60`}
                  data-testid={`upgrade-plan-${p.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display font-bold text-lg">{p.name}</div>
                    {isCurrent && <span className="font-mono-cal text-[9px] uppercase tracking-widest text-[#00A36C]">[CURRENT]</span>}
                  </div>
                  <div className="font-mono-cal text-xs text-[#666] mt-1">{p.max_managers} mgrs · {p.max_employees} empls</div>
                  <div className="font-display text-3xl font-bold mt-3">A${p.monthly_cost}<span className="text-sm text-[#666] font-normal">/mo</span></div>
                  {!isCurrent && (
                    <div className="mt-3 font-mono-cal text-xs text-[#FF4F00]">
                      {upgradingTo === p.id ? "Redirecting…" : <><CreditCard size={12} className="inline mr-1"/> Upgrade →</>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="cal-card p-6 mb-10" data-testid="org-join-code">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">[JOIN CODE]</div>
            <div className="font-display font-bold text-xl">Invite teammates with a personal email</div>
            <p className="text-sm text-[#666] mt-1">Share this code with managers using gmail/yahoo/etc. They'll join {org.name} pending your approval.</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <code className="font-mono-cal text-xl sm:text-2xl tracking-widest bg-[#111] text-white px-3 sm:px-4 py-2" data-testid="join-code-value">{org.join_code}</code>
            <button onClick={() => copy(org.join_code)} className="cal-btn cal-btn-ghost" data-testid="copy-join-code"><Copy size={14}/> Copy</button>
            <button onClick={rotateCode} disabled={rotating} className="cal-btn cal-btn-ghost" data-testid="rotate-join-code"><RefreshCw size={14}/> {rotating ? "…" : "Rotate"}</button>
          </div>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="font-display font-bold text-2xl mb-4">Pending approval ({pending.length})</h2>
        {pending.length === 0 ? (
          <div className="cal-card p-8 text-center font-mono-cal text-xs text-[#666]" data-testid="pending-empty">[ NONE WAITING ]</div>
        ) : (
          <div className="space-y-3">
            {pending.map((m) => (
              <div key={m.id} className="cal-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`pending-row-${m.id}`}>
                <div>
                  <div className="font-display font-bold text-lg">{m.name}</div>
                  <div className="text-sm text-[#666]">{m.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => act(m.id, () => api.post(`/org/managers/${m.id}/approve`), "Approved")}
                    disabled={busyId === m.id || overLimit}
                    title={overLimit ? "Manager limit reached for this plan" : ""}
                    className="cal-btn"
                    data-testid={`approve-${m.id}`}
                  >
                    <Check size={14}/> Approve
                  </button>
                  <button
                    onClick={() => act(m.id, () => api.delete(`/org/managers/${m.id}`), "Removed")}
                    disabled={busyId === m.id}
                    className="cal-btn cal-btn-ghost"
                    data-testid={`reject-${m.id}`}
                  >
                    <X size={14}/> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-bold text-2xl mb-4">Active managers ({active.length})</h2>
        <div className="space-y-3">
          {active.map((m) => (
            <div key={m.id} className="cal-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`active-row-${m.id}`}>
              <div>
                <div className="font-display font-bold text-lg">
                  {m.name}
                  {m.is_org_admin && <span className="ml-3 font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ADMIN]</span>}
                  {m.is_platform_owner && <span className="ml-3 font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C]">[PLATFORM]</span>}
                  {m.id === user.id && <span className="ml-3 font-mono-cal text-[10px] uppercase tracking-widest text-[#111]">[YOU]</span>}
                </div>
                <div className="text-sm text-[#666]">{m.email}</div>
              </div>
              {m.id === user.id ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowChangePw(true)}
                    className="cal-btn cal-btn-ghost"
                    title="Change your password"
                    data-testid="self-change-password"
                  >
                    <KeyRound size={14} /> Change password
                  </button>
                </div>
              ) : !m.is_platform_owner ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setResetConfirmId(m.id)}
                    disabled={busyId === m.id}
                    className="cal-btn cal-btn-ghost"
                    title={`Email a password reset link to ${m.email}`}
                    data-testid={`reset-password-${m.id}`}
                  >
                    <KeyRound size={14} /> Reset password
                  </button>
                  <button
                    onClick={() => act(m.id, () => api.post(`/org/managers/${m.id}/revoke`), "Access revoked")}
                    disabled={busyId === m.id}
                    className="cal-btn cal-btn-ghost"
                    data-testid={`revoke-${m.id}`}
                  >
                    Revoke
                  </button>
                  <button
                    onClick={() => act(m.id, () => api.delete(`/org/managers/${m.id}`), "Removed")}
                    disabled={busyId === m.id}
                    className="cal-btn cal-btn-ghost"
                    data-testid={`remove-${m.id}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <ChangePasswordModal
        open={showChangePw}
        onClose={() => setShowChangePw(false)}
        email={user?.email}
      />

      {resetConfirmId && (() => {
        const mgr = active.find((m) => m.id === resetConfirmId);
        if (!mgr) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            data-testid="reset-password-confirm-modal"
            onClick={(e) => { if (e.target === e.currentTarget && busyId !== mgr.id) setResetConfirmId(null); }}
          >
            <div className="bg-white w-full max-w-md border-2 border-[#111] shadow-xl">
              <div className="p-5 border-b border-[#EEE]">
                <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">[ RESET PASSWORD ]</div>
                <div className="font-display font-bold text-xl mt-1">{mgr.name}</div>
                <div className="text-sm text-[#666] font-mono-cal">{mgr.email}</div>
              </div>
              <div className="p-5 text-sm">
                We&apos;ll email <b>{mgr.email}</b> a one-hour password-reset link. Their current password stays valid until they use the link.
              </div>
              <div className="p-5 border-t border-[#EEE] flex justify-end gap-2">
                <button
                  onClick={() => setResetConfirmId(null)}
                  disabled={busyId === mgr.id}
                  className="cal-btn cal-btn-ghost"
                  data-testid="reset-password-cancel"
                >Cancel</button>
                <button
                  onClick={() => doResetPassword(mgr)}
                  disabled={busyId === mgr.id}
                  className="cal-btn"
                  data-testid="reset-password-confirm"
                >
                  {busyId === mgr.id ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
